// Customerspayment.js - EXACTLY like Addresses Management

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaSearch,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckDouble,
  FaEllipsisV,
  FaChevronDown,
  FaTimes,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { FaPlusCircle } from "react-icons/fa";
import { IoMdArrowDropdown } from 'react-icons/io';
import { RiDeleteBinFill } from 'react-icons/ri';
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/CurrencyContext';
import './Customerspayment.css';

// Last loaded payments of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const CP_CACHE_PREFIX = 'customerspayment_cache_';
const CP_CITIES_PREFIX = 'customerspayment_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getShopIdFromPath = (pathname) => {
  const match = pathname.match(/\/shop\/(\d+)/);
  return match ? match[1] : '';
};

const Customerspayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice, currency, loading: currencyLoading } = useCurrency();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly while fresh data loads in the background.
  const initialShopId = getShopIdFromPath(location.pathname);
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cachedPayments = initialShopId ? readJson(`${CP_CACHE_PREFIX}${initialShopId}`) : null;
    const cachedCities = initialShopId ? readJson(`${CP_CITIES_PREFIX}${initialShopId}`) : null;
    initialRef.current = {
      payments: Array.isArray(cachedPayments) ? cachedPayments : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [reference, setReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [isInvoiceDropdownOpen, setIsInvoiceDropdownOpen] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // loading = a save / delete is running (disables the buttons)
  const [loading, setLoading] = useState(false);
  // True only while there is nothing to show yet (so "No payments found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.payments);
  const [error, setError] = useState(null);

  const [selectedShopId, setSelectedShopId] = useState(initialShopId);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentShopName, setCurrentShopName] = useState('Loading...');

  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [city, setCity] = useState('');
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState(initialRef.current.payments || []);

  const paymentsRequestRef = useRef(0);
  const prevShopRef = useRef(initialShopId);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

  // ✅ City counts are calculated from the loaded payments
  const cityCounts = useMemo(() => {
    const counts = {};
    payments.forEach((p) => {
      if (p.city) counts[p.city] = (counts[p.city] || 0) + 1;
    });
    return counts;
  }, [payments]);

  const getAuthToken = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('accessToken');
    if (token) return token;
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData?.access_token || userData?.token || null;
  };

  const apiFetch = useCallback(async (url, options = {}) => {
    const token = getAuthToken();
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const headers = { 'Content-Type': 'application/json', ...options.headers };

    if (token) { headers['Authorization'] = `Bearer ${token}`; }

    const response = await fetch(fullUrl, { ...options, headers });

    if (!response.ok) {
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) { errorMessage = errorData.error; }
        else if (errorData.message) { errorMessage = errorData.message; }
      } catch (e) {}
      throw new Error(errorMessage);
    }

    return response.json();
  }, []);

  const fetchShops = async () => {
    try {
      const data = await apiFetch('/user/shops/');
      let shopsData = [];
      if (data.results) { shopsData = data.results; }
      else if (Array.isArray(data)) { shopsData = data; }
      else if (data.shops) { shopsData = data.shops; }
      setShops(shopsData);

      const match = location.pathname.match(/\/shop\/(\d+)/);
      if (!match && shopsData.length > 0) {
        const firstShop = shopsData[0];
        navigate(`/shop/${firstShop.shop_id}/Customerspayment`, { replace: true });
      } else if (!match) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      if (!initialShopId) setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiFetch('/user/users/');
      let usersData = [];
      if (data.results) { usersData = data.results; }
      else if (Array.isArray(data)) { usersData = data; }
      else if (data.users) { usersData = data.users; }
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchCities = useCallback(async () => {
    if (!selectedShopId) return;
    // The dropdown only shows "Loading..." when there is no saved list yet
    const hasSaved = Array.isArray(readJson(`${CP_CITIES_PREFIX}${selectedShopId}`));
    try {
      if (!hasSaved) setLoadingFilterCities(true);
      const data = await apiFetch(`/order/shop-cities/?shop_id=${selectedShopId}`);

      let citiesData = [];
      if (data) {
        if (Array.isArray(data.cities)) citiesData = data.cities;
        else if (Array.isArray(data)) citiesData = data;
        else if (typeof data.cities === 'string') {
          try { citiesData = JSON.parse(data.cities); }
          catch { citiesData = data.cities.split(',').map(c => c.trim()).filter(c => c); }
        }
      }

      setCities(citiesData);
      setFilterCities(citiesData);
      writeJson(`${CP_CITIES_PREFIX}${selectedShopId}`, citiesData);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
      setFilterCities([]);
    } finally {
      setLoadingFilterCities(false);
    }
  }, [selectedShopId, apiFetch]);

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
  };

  const fetchInvoices = useCallback(async () => {
    if (!selectedShopId) return;
    try {
      const url = `/order/invoices/all/?shop_id=${selectedShopId}`;
      const data = await apiFetch(url);
      if (data) {
        const formattedInvoices = data.map(invoice => ({
          id: invoice.id || invoice.invoice_number,
          invoiceNumber: invoice.invoice_number,
          orderNumber: invoice.order_details?.order_Tracking_Id || 'N/A',
          displayText: `Invoice #${invoice.invoice_number} for Order ${invoice.order_details?.order_Tracking_Id || 'N/A'}`
        }));
        setInvoices(formattedInvoices);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to fetch invoices');
    }
  }, [selectedShopId, apiFetch]);

  // ✅ UPDATED: Handle new response structure with city_counts
  // Runs in the background: it never disables the buttons, and only the latest
  // request is allowed to update the screen.
  const fetchPayments = useCallback(async () => {
    if (!selectedShopId) return;
    const requestId = ++paymentsRequestRef.current;
    try {
      const url = `/order/api/customer-payments/?shop_id=${selectedShopId}`;
      const data = await apiFetch(url);
      if (requestId !== paymentsRequestRef.current) return;

      // ✅ Handle new response structure
      let paymentsData = [];

      if (data && data.payments) {
        paymentsData = data.payments;
      } else if (Array.isArray(data)) {
        paymentsData = data;
      }

      const formattedPayments = paymentsData.map(payment => {
        let accountDisplay = 'Unknown';
        if (payment.account_details) {
          const details = payment.account_details;
          const fullName = `${details.first_Name || ''} ${details.last_Name || ''}`.trim();
          accountDisplay = fullName || details.email || 'Unknown';
        } else if (payment.account_username) {
          accountDisplay = payment.account_username;
        } else if (payment.account) {
          accountDisplay = String(payment.account);
        }

        return {
          id: payment.customer_payment_id,
          account: accountDisplay,
          accountId: payment.account,
          accountObj: payment.account_details || payment.account,
          amount: payment.amount,
          paymentDate: payment.payment_date,
          reference: payment.reference_no,
          paymentMethod: payment.payment_method,
          city: payment.city || '',
          invoice: payment.invoices?.[0]?.invoice_number || 'N/A',
          createdAt: payment.created_at,
          updatedAt: payment.updated_at,
          timestamps: payment.created_at,
          shop_id: payment.shop_id,
          shop_name: payment.shop_name || payment.shop_details?.shop_name || 'N/A'
        };
      });

      setPayments(formattedPayments);
      writeJson(`${CP_CACHE_PREFIX}${selectedShopId}`, formattedPayments);
      setError(null);
    } catch (error) {
      if (requestId !== paymentsRequestRef.current) return;
      console.error('Error fetching payments:', error);
      setError('Failed to fetch payments: ' + error.message);
    } finally {
      if (requestId === paymentsRequestRef.current) setIsLoading(false);
    }
  }, [selectedShopId, apiFetch]);

  useEffect(() => {
    if (selectedShopId) {
      const shopUsers = users.filter(user => user.shop_id === parseInt(selectedShopId) || user.shop === parseInt(selectedShopId));
      setFilteredUsers(shopUsers);
    } else {
      setFilteredUsers([]);
    }
  }, [selectedShopId, users]);

  // Shops and users are loaded once
  useEffect(() => {
    fetchShops();
    fetchUsers();
  }, []);

  useEffect(() => {
    const shopIdFromUrl = getShopIdFromPath(location.pathname);
    if (shopIdFromUrl && shopIdFromUrl !== selectedShopId) {
      setSelectedShopId(shopIdFromUrl);
    }
  }, [location.pathname]);

  // Shop name
  useEffect(() => {
    if (!selectedShopId) return;
    const shop = shops.find(s => String(s.shop_id) === String(selectedShopId));
    if (shop) { setCurrentShopName(shop.shop_name); }
    else { setCurrentShopName(`Shop ${selectedShopId}`); }
  }, [selectedShopId, shops]);

  // Data of the shop: show the saved list of the shop immediately, then refresh it.
  // Cities, invoices and payments load together instead of one after another.
  useEffect(() => {
    if (!selectedShopId) return;

    if (prevShopRef.current !== selectedShopId) {
      prevShopRef.current = selectedShopId;
      const cachedPayments = readJson(`${CP_CACHE_PREFIX}${selectedShopId}`);
      const cachedCities = readJson(`${CP_CITIES_PREFIX}${selectedShopId}`);
      if (Array.isArray(cachedPayments)) {
        setPayments(cachedPayments);
        setIsLoading(false);
      } else {
        setPayments([]);
        setIsLoading(true);
      }
      const savedCities = Array.isArray(cachedCities) ? cachedCities : [];
      setCities(savedCities);
      setFilterCities(savedCities);
      setSelectedFilterCity('');
      setSelectedPayments([]);
      setInvoices([]);
    }

    fetchCities();
    fetchInvoices();
    fetchPayments();
  }, [selectedShopId, fetchCities, fetchInvoices, fetchPayments]);

  const filteredInvoices = invoices.filter(invoice =>
    invoice.displayText.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
  );

  const handleSearch = e => { setSearchTerm(e.target.value); };

  const filteredPayments = payments.filter(payment => {
    if (selectedFilterCity && selectedFilterCity.trim() !== '') {
      if (payment.city !== selectedFilterCity) {
        return false;
      }
    }

    const accountString = String(payment.account || '');
    const referenceString = String(payment.reference || '');
    return accountString.toLowerCase().includes(searchTerm.toLowerCase()) ||
           referenceString.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSelectAll = () => {
    if (selectedPayments.length === filteredPayments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredPayments.map(payment => payment.id));
    }
    setShowActionDropdown(false);
  };

  const handleSelectPayment = paymentId => {
    if (selectedPayments.includes(paymentId)) {
      setSelectedPayments(selectedPayments.filter(id => id !== paymentId));
    } else {
      setSelectedPayments([...selectedPayments, paymentId]);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedPayments.length === 0) {
      await Swal.fire({ icon: 'warning', title: 'No Selection', text: 'Please select payments to delete', confirmButtonColor: '#004aad' });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Selected Payments?',
      text: `Are you sure you want to delete ${selectedPayments.length} payment(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        for (const paymentId of selectedPayments) {
          await apiFetch(`/order/api/customer-payments/${paymentId}/`, { method: 'DELETE' });
        }
        await fetchPayments();
        setSelectedPayments([]);
        setShowActionDropdown(false);
        await Swal.fire({ icon: 'success', title: 'Deleted!', text: `${selectedPayments.length} payment(s) deleted successfully!`, timer: 2000, showConfirmButton: false });
      } catch (error) {
        console.error('Error deleting payments:', error);
        await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete payments. Please try again.', confirmButtonColor: '#004aad' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteSingle = async (payment) => {
    const result = await Swal.fire({
      title: 'Delete Payment?',
      text: `Are you sure you want to delete payment for "${payment.account}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await apiFetch(`/order/api/customer-payments/${payment.id}/`, { method: 'DELETE' });
        await fetchPayments();
        setSelectedPayments(prev => prev.filter(id => id !== payment.id));

        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Payment deleted successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting payment:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: 'Failed to delete payment. Please try again.',
          confirmButtonColor: '#004aad'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClearAll = () => { setSelectedPayments([]); setShowActionDropdown(false); };

  const handleOpenModalForNew = () => {
    setEditingId(null);
    setAccount('');
    setAmount('');
    setPaymentDate('');
    setReference('');
    setPaymentMethod('');
    setCity('');
    setSelectedInvoice('');
    setInvoiceSearchTerm('');
    setIsInvoiceDropdownOpen(false);
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = payment => {
    setEditingId(payment.id);
    setAccount(payment.accountId || payment.account || '');
    setAmount(payment.amount || '');
    setPaymentDate(payment.paymentDate);
    setReference(payment.reference || '');
    setPaymentMethod(payment.paymentMethod || '');
    setCity(payment.city || '');
    setSelectedInvoice(payment.invoice || '');
    setInvoiceSearchTerm('');
    setIsInvoiceDropdownOpen(false);
    setIsModalOpen(true);
  };

  const handleSelectInvoice = invoice => {
    setSelectedInvoice(invoice.displayText);
    setInvoiceSearchTerm('');
    setIsInvoiceDropdownOpen(false);
  };

  const handleSave = async () => {
    if (!account.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select an account', confirmButtonColor: '#004aad' });
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please enter a valid amount', confirmButtonColor: '#004aad' });
      return;
    }
    if (!paymentDate.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select payment date', confirmButtonColor: '#004aad' });
      return;
    }
    if (!paymentMethod.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select payment method', confirmButtonColor: '#004aad' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let invoiceNumbers = [];
      if (selectedInvoice) {
        const match = selectedInvoice.match(/#([A-Za-z0-9-]+)/);
        if (match && match[1]) { invoiceNumbers = [match[1]]; }
      }

      const paymentData = {
        shop_id: parseInt(selectedShopId),
        account: account.trim(),
        amount: parseFloat(amount),
        payment_date: paymentDate,
        reference_number: reference.trim(),
        payment_method: paymentMethod,
        city: city || null,
        invoice_ids: invoiceNumbers
      };

      if (editingId !== null) {
        await apiFetch(`/order/api/customer-payments/${editingId}/`, {
          method: 'PUT',
          body: JSON.stringify(paymentData)
        });
        await Swal.fire({ icon: 'success', title: 'Updated!', text: `Payment updated successfully for ${currentShopName}!`, timer: 2000, showConfirmButton: false });
      } else {
        await apiFetch('/order/api/customer-payments/', {
          method: 'POST',
          body: JSON.stringify(paymentData)
        });
        await Swal.fire({ icon: 'success', title: 'Added!', text: `Payment added successfully for ${currentShopName}!`, timer: 2000, showConfirmButton: false });
      }

      await fetchPayments();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving payment:', error);
      setError(error.message || 'Failed to save payment. Please try again.');
      await Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Failed to save payment. Please try again.', confirmButtonColor: '#004aad' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (editingId === null) {
      await Swal.fire({ icon: 'warning', title: 'No Selection', text: 'No payment selected for deletion', confirmButtonColor: '#004aad' });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Payment?',
      text: `Are you sure you want to delete payment for "${account}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await apiFetch(`/order/api/customer-payments/${editingId}/`, { method: 'DELETE' });
        await fetchPayments();
        setSelectedPayments(selectedPayments.filter(id => id !== editingId));
        await Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Payment deleted successfully!', timer: 2000, showConfirmButton: false });
        handleCloseModal();
      } catch (error) {
        console.error('Error deleting payment:', error);
        await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete payment. Please try again.', confirmButtonColor: '#004aad' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAccount('');
    setAmount('');
    setPaymentDate('');
    setReference('');
    setPaymentMethod('');
    setCity('');
    setSelectedInvoice('');
    setInvoiceSearchTerm('');
    setIsInvoiceDropdownOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleClickOutside = useCallback(e => {
    if (showActionDropdown && !e.target.closest('.cp-action-dropdown-container')) {
      setShowActionDropdown(false);
    }
    if (isInvoiceDropdownOpen && !e.target.closest('.cp-invoice-dropdown-container')) {
      setIsInvoiceDropdownOpen(false);
      setInvoiceSearchTerm('');
    }
  }, [showActionDropdown, isInvoiceDropdownOpen]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => { document.removeEventListener('click', handleClickOutside); };
  }, [handleClickOutside]);

  const isOperationInProgress = loading || currencyLoading;

  return (
    <div className="cp-main-container">
      {/* ✅ HEADER - EXACTLY Addresses Management */}
      <div className="cp-header-section">
        <div className="cp-header-left">
          <FaPlusCircle className="cp-header-icon" />
          <h2 className="cp-header-title">Customer Payments</h2>
          {/* ✅ City Badge — ALWAYS shown; "All Cities" when no city selected */}
          <span className="cp-city-badge">
            <FaMapMarkerAlt className="cp-city-icon" />
            <span className="cp-city-name">
              {selectedFilterCity
                ? `${selectedFilterCity} (${cityCounts[selectedFilterCity] || 0})`
                : `All Cities (${filteredPayments.length})`}
            </span>
          </span>
        </div>

        <div className="cp-header-right">
          {/* City Dropdown with Count */}
          <div className="cp-city-dropdown-wrapper">
            <select
              className="cp-city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={loadingFilterCities || filterCities.length === 0}
            >
              {/* ✅ Default option = All Cities */}
              <option value="">
                {loadingFilterCities ? 'Loading...' : 'All Cities'}
              </option>
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="cp-city-dropdown-icon" />
          </div>

          {/* Search */}
          <div className="cp-search-wrapper">
            <FaSearch className="cp-search-icon" />
            <input
              type="text"
              placeholder="Search by account or reference..."
              value={searchTerm}
              onChange={handleSearch}
              className="cp-search-input"
              disabled={isOperationInProgress}
            />
          </div>

          {/* Select All */}
          <button className="cp-select-all-btn" onClick={handleSelectAll} disabled={isOperationInProgress}>
            <FaCheckDouble />
            <span>{selectedPayments.length === filteredPayments.length && filteredPayments.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>

          {/* Add Payment */}
          <button className="cp-add-payment-btn" onClick={handleOpenModalForNew} disabled={isOperationInProgress}>
            <FaPlus />
            <span>ADD PAYMENT</span>
          </button>
        </div>
      </div>

      {/* ... baaki sab content same rahega ... */}
      <div className="cp-content">
        {error && (
          <div className="cp-error-message">{error}</div>
        )}

        {selectedPayments.length > 0 && (
          <div className="cp-selected-summary">
            <div className="cp-summary-content">
              <span className="cp-summary-icon">✓</span>
              <span className="cp-summary-text">
                {selectedPayments.length} payment(s) selected
              </span>
              <div className="cp-action-dropdown-container">
                <button
                  className="cp-action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                  disabled={isOperationInProgress}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="cp-action-dropdown-menu">
                    <button
                      className="cp-dropdown-item cp-delete-item"
                      onClick={handleDeleteSelected}
                      disabled={isOperationInProgress}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedPayments.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="cp-clear-selection" onClick={handleClearAll} disabled={isOperationInProgress}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="cp-table-container">
          <div className="cp-table-wrapper">
            <table className="cp-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input type="checkbox" checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0} onChange={handleSelectAll} disabled={isOperationInProgress} />
                  </th>
                  <th>Account</th>
                  <th>City</th>
                  <th>Amount ({currency})</th>
                  <th>Payment Date</th>
                  <th>Reference</th>
                  <th>Payment Method</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so "No payments found" never flashes */}
                {isLoading ? (
                  <tr>
                    <td colSpan="8" style={{ height: '200px' }}></td>
                  </tr>
                ) : filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="cp-no-data-cell">
                      {selectedFilterCity
                        ? `No payments found in "${selectedFilterCity}".`
                        : 'No payments found.'}
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map(payment => (
                    <tr key={payment.id} className={selectedPayments.includes(payment.id) ? 'cp-selected' : ''}>
                      <td>
                        <input type="checkbox" checked={selectedPayments.includes(payment.id)} onChange={() => handleSelectPayment(payment.id)} disabled={isOperationInProgress} />
                      </td>
                      <td className="cp-account-cell">{payment.account || '-'}</td>
                      <td className="cp-city-cell">
                        {payment.city ? payment.city : '-'}
                      </td>
                      <td className="cp-amount-cell">{formatPrice(payment.amount)}</td>
                      <td>{payment.paymentDate}</td>
                      <td>{payment.reference || '-'}</td>
                      <td>{payment.paymentMethod || '-'}</td>
                      <td className="cp-actions-cell">
                        <div className="cp-actions-cell-inner">
                          <button className="cp-edit-payment-btn" onClick={() => handleOpenModalForEdit(payment)} title="Edit Payment" disabled={isOperationInProgress}>
                            <FaEdit />
                          </button>
                          <button className="cp-delete-payment-btn" onClick={() => handleDeleteSingle(payment)} title="Delete Payment" disabled={isOperationInProgress}>
                            <RiDeleteBinFill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal same rahega */}
      {isModalOpen && (
        <div className="cp-modal-overlay" onClick={handleCloseModal}>
          <div className="cp-modal-content" onClick={e => e.stopPropagation()}>
            <div className="cp-modal-header">
              <h3>{editingId !== null ? 'Edit Payment' : 'Add Payment'}</h3>
              <button className="cp-modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <div className="cp-modal-body">
              {error && <div className="cp-error-message">{error}</div>}

              <form className="cp-payment-form" onSubmit={e => e.preventDefault()}>
                <div className="cp-form-group">
                  <label>Shop</label>
                  <div className="cp-shop-name-display">
                    <strong>{currentShopName}</strong>
                  </div>
                </div>

                {/* ✅ 2 FIELDS IN ONE LINE (Account + City) */}
                <div className="cp-form-row">
                  <div className="cp-form-group">
                    <label>Account *</label>
                    <select value={account} onChange={e => setAccount(e.target.value)} className="cp-form-select" required disabled={isOperationInProgress}>
                      <option value="">Select Account</option>
                      {filteredUsers.length === 0 ? (
                        <option value="" disabled>No users found for this shop</option>
                      ) : (
                        filteredUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.first_Name} {user.last_Name} ({user.email})
                          </option>
                        ))
                      )}
                    </select>
                    <p className="cp-field-hint">{filteredUsers.length} user(s) available for {currentShopName}</p>
                  </div>

                  <div className="cp-form-group">
                    <label>CITY:</label>
                    <select value={city} onChange={e => setCity(e.target.value)} className="cp-form-select" disabled={isOperationInProgress || cities.length === 0}>
                      <option value="">Select City</option>
                      {cities.map((cityName, index) => (
                        <option key={index} value={cityName}>{cityName}</option>
                      ))}
                    </select>
                    {cities.length === 0 && (
                      <p className="cp-field-hint">No cities available for this shop. Please add cities in shop settings.</p>
                    )}
                  </div>
                </div>

                {/* ✅ 2 FIELDS IN ONE LINE (Amount + Payment Date) */}
                <div className="cp-form-row">
                  <div className="cp-form-group">
                    <label>Amount ({currency}) *</label>
                    <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={`Enter amount in ${currency}`} step="0.01" min="0.01" required disabled={isOperationInProgress} />
                  </div>

                  <div className="cp-form-group">
                    <label>Payment Date *</label>
                    <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} required disabled={isOperationInProgress} />
                  </div>
                </div>

                {/* ✅ 2 FIELDS IN ONE LINE (Reference + Payment Method) */}
                <div className="cp-form-row">
                  <div className="cp-form-group">
                    <label>Reference</label>
                    <input type="text" value={reference} onChange={e => setReference(e.target.value)} placeholder="Enter reference number" disabled={isOperationInProgress} />
                  </div>

                  <div className="cp-form-group">
                    <label>Payment Method *</label>
                    <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="cp-payment-method-select" required disabled={isOperationInProgress}>
                      <option value="">Select Payment Method</option>
                      <option value="Cash">Cash</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                </div>

                <div className="cp-form-group">
                  <label>Invoice</label>
                  <div className="cp-invoice-dropdown-container">
                    <div className="cp-invoice-dropdown-input" onClick={() => !isOperationInProgress && setIsInvoiceDropdownOpen(!isInvoiceDropdownOpen)}>
                      <input type="text" placeholder="Select Invoice" value={selectedInvoice} readOnly className="cp-invoice-input" disabled={isOperationInProgress} />
                      <FaChevronDown className={`cp-dropdown-arrow ${isInvoiceDropdownOpen ? 'open' : ''}`} />
                    </div>

                    {isInvoiceDropdownOpen && !isOperationInProgress && (
                      <div className="cp-invoice-dropdown-menu">
                        <div className="cp-invoice-search">
                          <FaSearch className="cp-search-icon-small" />
                          <input type="text" placeholder="Filter invoices..." value={invoiceSearchTerm} onChange={e => setInvoiceSearchTerm(e.target.value)} className="cp-invoice-search-input" autoFocus />
                          {invoiceSearchTerm && <FaTimes onClick={() => setInvoiceSearchTerm('')} />}
                        </div>

                        <div className="cp-invoice-list">
                          {filteredInvoices.length === 0 ? (
                            <div className="cp-no-invoices">No invoices found</div>
                          ) : (
                            filteredInvoices.map(invoice => (
                              <div key={invoice.id} className={`cp-invoice-item ${selectedInvoice === invoice.displayText ? 'selected' : ''}`} onClick={() => handleSelectInvoice(invoice)}>
                                {invoice.displayText}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </form>
            </div>

            <div className="cp-modal-footer">
              <button className="cp-btn-cancel" onClick={handleCloseModal} disabled={isOperationInProgress}>Cancel</button>
              <button className="cp-btn-save" onClick={handleSave} disabled={isOperationInProgress}>
                {loading ? 'Saving...' : editingId !== null ? 'Update Payment' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customerspayment;